import simpleGit, { SimpleGit } from 'simple-git';
import path from 'node:path';
import fs from 'node:fs';
import os from 'node:os';
import { ConfigManager, RepoConfig } from './config';

export class MultiRepoMerge {
  private configManager: ConfigManager;
  private tempDir: string;

  constructor() {
    this.configManager = new ConfigManager();
    this.tempDir = path.join(os.tmpdir(), 'supos-git-merge');
    if (!fs.existsSync(this.tempDir)) {
      fs.mkdirSync(this.tempDir, { recursive: true });
    }
  }

  async initRepo(config: RepoConfig): Promise<SimpleGit> {
    const repoDir = path.join(this.tempDir, config.name);
    if (!fs.existsSync(repoDir)) {
      fs.mkdirSync(repoDir, { recursive: true });
    }

    const git = simpleGit(repoDir);

    // 添加进度监听器
    // git.outputHandler((_, stdout, stderr) => {
    //   stdout.pipe(process.stdout);
    //   stderr.pipe(process.stderr);
    // });

    try {
      // 初始化Git仓库（如果不存在）
      if (!fs.existsSync(path.join(repoDir, '.git'))) {
        await git.init();
      }

      // 配置认证信息
      let remoteUrlString = config.url;
      try {
        const remoteUrl = new URL(config.url);
        // 移除现有的认证信息（如果有）
        remoteUrl.username = '';
        remoteUrl.password = '';

        // 重新构建URL，将认证信息放在正确的位置
        if (config.token) {
          remoteUrlString = remoteUrl
            .toString()
            .replace(/^(https?:\/\/)(.+)$/, `$1${encodeURIComponent(config.token)}@$2`);
        } else if (config.username && config.password) {
          remoteUrlString = remoteUrl
            .toString()
            .replace(
              /^(https?:\/\/)(.+)$/,
              `$1${encodeURIComponent(config.username)}:${encodeURIComponent(config.password)}@$2`
            );
        }
      } catch (error) {
        console.warn(`[${config.name}] URL解析失败，使用原始URL`);
      }

      // 确保origin远程仓库已配置
      try {
        const currentUrl = await git.remote(['get-url', 'origin']);
        if (currentUrl?.trim() !== remoteUrlString.trim()) {
          await git.remote(['set-url', 'origin', remoteUrlString]);
        }
      } catch {
        await git.remote(['add', 'origin', remoteUrlString]);
      }

      // 使用更强的fetch参数，确保获取所有远程分支信息
      await git.fetch(['--all', '--prune', '--tags', '--force']);
      return git;
    } catch (error: any) {
      const errorMessage = error.message || '未知错误';
      const gitError = error.git ? `\nGit错误: ${JSON.stringify(error.git, null, 2)}` : '';
      throw new Error(`[${config.name}] Git操作失败: ${errorMessage}${gitError}`);
    }
  }

  async mergeCrossBranch(
    sourceRepo: string,
    sourceBranch: string,
    targetRepo: string,
    targetBranch: string
  ): Promise<void> {
    const sourceConfig = this.configManager.getRepo(sourceRepo);
    const targetConfig = this.configManager.getRepo(targetRepo);

    if (!sourceConfig || !targetConfig) {
      throw new Error('源仓库或目标仓库配置不存在');
    }

    const sourceGit = await this.initRepo(sourceConfig);
    const targetGit = await this.initRepo(targetConfig);

    try {
      // 在源仓库中获取最新代码并检查分支是否存在
      await sourceGit.fetch(['--all', '--prune', '--tags', '--force']);

      // 检查源分支是否存在
      const sourceBranchExists = await this.checkRemoteBranchExists(sourceRepo, sourceBranch, sourceGit);
      if (!sourceBranchExists) {
        throw new Error(`源分支 '${sourceBranch}' 在仓库 '${sourceRepo}' 中不存在`);
      }

      const normalizedSourceBranch = sourceBranch.replace(/^(origin\/|remotes\/origin\/|source_[^/]+\/)/, '');

      // 切换到源分支
      try {
        // 先尝试直接切换到本地分支
        await sourceGit.checkout(normalizedSourceBranch);
      } catch (error) {
        // 如果本地分支不存在，创建并跟踪远程分支
        try {
          await sourceGit.checkout(['-b', normalizedSourceBranch, '--track', `origin/${normalizedSourceBranch}`]);
        } catch (checkoutError) {
          console.error(`[${sourceRepo}] 创建跟踪分支失败:`, checkoutError);
          // 最后尝试直接获取远程分支内容
          await sourceGit.fetch(['origin', normalizedSourceBranch, '--update-head-ok']);
          // 创建本地分支但不设置跟踪
          await sourceGit.checkout(['-b', normalizedSourceBranch]);
        }
      }

      // 确保分支内容是最新的
      try {
        await sourceGit.pull('origin', normalizedSourceBranch);
      } catch (pullError) {
        console.warn(`[${sourceRepo}] 拉取更新失败，继续使用当前内容:`, pullError);
      }

      // 在目标仓库中设置源仓库为远程仓库
      const sourceRemoteName = `source_${sourceRepo}`;
      try {
        await targetGit.remote(['remove', sourceRemoteName]);
      } catch (error) {
        // 忽略移除不存在的远程仓库时的错误
      }
      // 使用源仓库的认证信息构建URL
      const sourceUrl = new URL(sourceConfig.url);
      if (sourceConfig.token) {
        sourceUrl.username = encodeURIComponent(sourceConfig.token);
        sourceUrl.password = '';
      } else if (sourceConfig.username && sourceConfig.password) {
        sourceUrl.username = encodeURIComponent(sourceConfig.username);
        sourceUrl.password = encodeURIComponent(sourceConfig.password);
      }

      await targetGit.remote(['add', sourceRemoteName, sourceUrl.toString()]);
      await targetGit.fetch([sourceRemoteName, '--force']);
      // 检查目标分支是否存在
      await targetGit.fetch(['--all', '--prune', '--tags', '--force']);
      const targetBranches = await targetGit.branch(['--all']);
      const normalizedTargetBranch = targetBranch.replace(/^origin\//, '');
      let targetBranchExists = targetBranches.all.some((branch) => {
        const normalizedBranch = branch
          .trim()
          .replace(/^remotes\/origin\//, '')
          .replace(/^origin\//, '');
        const result = normalizedBranch === normalizedTargetBranch;
        return result;
      });

      if (!targetBranchExists) {
        // 尝试直接检查远程分支是否存在
        const remoteBranches = await targetGit.listRemote(['--heads']);
        targetBranchExists = remoteBranches.includes(`refs/heads/${normalizedTargetBranch}`);
        if (!targetBranchExists) {
          throw new Error(
            `目标分支 '${targetBranch}' 在仓库 '${targetRepo}' 中不存在\n可用的远程分支:\n${targetBranches.all.join('\n')}`
          );
        }

        // 如果远程分支存在但本地没有，先创建本地跟踪分支
        await targetGit.fetch(['origin', normalizedTargetBranch]);
      }

      // 切换到目标分支
      try {
        // 先尝试直接切换到本地分支
        await targetGit.checkout(normalizedTargetBranch);
      } catch (error) {
        // 如果本地分支不存在，创建并跟踪远程分支
        try {
          await targetGit.checkout(['-b', normalizedTargetBranch, '--track', `origin/${normalizedTargetBranch}`]);
        } catch (checkoutError) {
          console.error(`[${targetRepo}] 创建跟踪分支失败:`, checkoutError);
          // 最后尝试直接获取远程分支内容
          await targetGit.fetch(['origin', normalizedTargetBranch, '--update-head-ok']);
          // 创建本地分支但不设置跟踪
          await targetGit.checkout(['-b', normalizedTargetBranch]);
        }
      }

      // 确保分支内容是最新的
      try {
        await targetGit.pull('origin', normalizedTargetBranch);
      } catch (pullError) {
        console.warn(`[${targetRepo}] 拉取更新失败，继续使用当前内容:`, pullError);
      }
      try {
        // 尝试合并源分支
        await targetGit.merge([`${sourceRemoteName}/${normalizedSourceBranch}`]);
        // 推送合并结果
        await targetGit.push('origin', normalizedTargetBranch);
      } catch (error: any) {
        console.error(`[${targetRepo}] 合并失败:`, error.message);
        throw new Error(`分支合并失败: ${error.message}`);
      }
      // 清理临时远程仓库
      await targetGit.remote(['remove', sourceRemoteName]);
    } catch (error: any) {
      const errorMessage = error.message || '未知错误';
      const gitError = error.git ? `\nGit错误: ${JSON.stringify(error.git, null, 2)}` : '';
      throw new Error(`跨仓库合并失败: ${errorMessage}${gitError}`);
    }
  }

  async abortMerge(repo: string): Promise<void> {
    const config = this.configManager.getRepo(repo);
    if (!config) {
      throw new Error('仓库配置不存在');
    }
    const git = await this.initRepo(config);
    try {
      await git.merge(['--abort']);
    } catch (error: any) {
      const errorMessage = error.message || '未知错误';
      const gitError = error.git ? `\nGit错误: ${JSON.stringify(error.git, null, 2)}` : '';
      throw new Error(`中止合并失败: ${errorMessage}${gitError}`);
    }
  }

  async resolveConflicts(repo: string, strategy: 'ours' | 'theirs'): Promise<void> {
    const config = this.configManager.getRepo(repo);
    if (!config) {
      throw new Error('仓库配置不存在');
    }

    const git = await this.initRepo(config);
    try {
      if (strategy === 'ours') {
        await git.checkout(['--ours', '.']);
      } else {
        await git.checkout(['--theirs', '.']);
      }
      await git.add('.');
      console.log(`[${repo}] 冲突已解决`);
    } catch (error: any) {
      const errorMessage = error.message || '未知错误';
      const gitError = error.git ? `\nGit错误: ${JSON.stringify(error.git, null, 2)}` : '';
      throw new Error(`解决冲突失败: ${errorMessage}${gitError}`);
    }
  }

  /**
   * 合并两个远程分支
   * 相比mergeCrossBranch，此方法更专注于远程分支合并，减少了本地分支操作
   * @param sourceRepo 源仓库名称
   * @param sourceBranch 源分支名称
   * @param targetRepo 目标仓库名称
   * @param targetBranch 目标分支名称
   * @param options 合并选项
   */
  async mergeRemoteBranches(
    sourceRepo: string,
    sourceBranch: string,
    targetRepo: string,
    targetBranch: string,
    options: {
      /** 是否在合并前显示差异 */
      showDiff?: boolean;
      /** 合并冲突时的自动解决策略 */
      conflictStrategy?: 'ours' | 'theirs';
      /** 是否推送合并结果 */
      push?: boolean;
    } = {}
  ): Promise<void> {
    const sourceConfig = this.configManager.getRepo(sourceRepo);
    const targetConfig = this.configManager.getRepo(targetRepo);

    if (!sourceConfig || !targetConfig) {
      throw new Error('源仓库或目标仓库配置不存在');
    }

    // 规范化分支名称，移除可能的前缀
    const normalizedSourceBranch = sourceBranch.replace(/^(remotes\/)?origin\//, '');
    const normalizedTargetBranch = targetBranch.replace(/^(remotes\/)?origin\//, '');

    const targetGit = await this.initRepo(targetConfig);
    const sourceRemoteName = `source_${sourceRepo}`;

    try {
      // 1. 在目标仓库中设置源仓库为远程仓库
      try {
        await targetGit.remote(['remove', sourceRemoteName]);
      } catch (error) {
        // 忽略移除不存在的远程仓库时的错误
      }

      // 使用源仓库的认证信息构建URL
      const sourceUrl = new URL(sourceConfig.url);
      if (sourceConfig.token) {
        sourceUrl.username = encodeURIComponent(sourceConfig.token);
        sourceUrl.password = '';
      } else if (sourceConfig.username && sourceConfig.password) {
        sourceUrl.username = encodeURIComponent(sourceConfig.username);
        sourceUrl.password = encodeURIComponent(sourceConfig.password);
      }

      await targetGit.remote(['add', sourceRemoteName, sourceUrl.toString()]);

      // 2. 获取源仓库和目标仓库的最新代码
      await targetGit.fetch(['--all', '--prune', '--tags']);
      await targetGit.fetch([sourceRemoteName, '--force']);

      // 3. 验证分支是否存在
      const remoteBranches = await targetGit.listRemote(['--heads']);

      // 检查源分支是否存在
      const sourceExists =
        (await this.checkRemoteBranchExists(sourceRemoteName, normalizedSourceBranch, targetGit)) ||
        remoteBranches.includes(`refs/heads/${normalizedSourceBranch}`);
      if (!sourceExists) {
        // 尝试直接检查源仓库中的分支
        const sourceGit = await this.initRepo(sourceConfig);
        const sourceBranchExists = await this.checkRemoteBranchExists(sourceRepo, normalizedSourceBranch, sourceGit);
        if (!sourceBranchExists) {
          throw new Error(`源分支 '${sourceBranch}' 在仓库 '${sourceRepo}' 中不存在`);
        }
      }

      // 检查目标分支是否存在
      const targetExists = remoteBranches.includes(`refs/heads/${normalizedTargetBranch}`);
      if (!targetExists) {
        throw new Error(`目标分支 '${targetBranch}' 在仓库 '${targetRepo}' 中不存在`);
      }

      // 4. 切换到目标分支
      try {
        await targetGit.checkout(normalizedTargetBranch);
      } catch (error) {
        // 如果本地分支不存在，创建跟踪分支
        await targetGit.checkout(['-b', normalizedTargetBranch, '--track', `origin/${normalizedTargetBranch}`]);
      }

      // 确保目标分支是最新的
      await targetGit.pull('origin', normalizedTargetBranch);

      // 5. 显示将要合并的差异（如果需要）
      if (options.showDiff) {
        console.log(`[${targetRepo}] 显示将要合并的差异...`);
        const diff = await targetGit.diff([`HEAD`, `${sourceRemoteName}/${normalizedSourceBranch}`]);
        console.log(diff);
      }

      // 6. 执行合并
      try {
        const mergeResult = await targetGit.merge([`${sourceRemoteName}/${normalizedSourceBranch}`]);
        // 检查是否已经是最新的（Already up to date）
        const hasRealChanges =
          mergeResult.summary.changes > 0 || mergeResult.summary.insertions > 0 || mergeResult.summary.deletions > 0;
        if (!hasRealChanges) {
          console.log(`[${targetRepo}] 分支已经是最新的，无需合并`);
          // 9. 清理临时远程仓库
          console.log(`[${targetRepo}] 清理临时远程仓库...`);
          await targetGit.remote(['remove', sourceRemoteName]);
          return;
        }
        // 7. 如果有冲突且指定了冲突解决策略，自动解决冲突
        if (options.conflictStrategy) {
          const status = await targetGit.status();
          if (status.conflicted.length > 0) {
            console.log(
              `[${targetRepo}] 检测到${status.conflicted.length}个冲突文件，使用${options.conflictStrategy === 'ours' ? '我们的' : '他们的'}策略解决...`
            );
            await this.resolveConflicts(targetRepo, options.conflictStrategy);
            await targetGit.commit(
              `自动合并 ${sourceRepo}/${normalizedSourceBranch} 到 ${targetRepo}/${normalizedTargetBranch}`
            );
          }
        }

        // 8. 推送合并结果（如果需要）
        if (options.push !== false) {
          // 默认推送
          console.log(`[${targetRepo}] 推送合并结果...`);
          await targetGit.push('origin', normalizedTargetBranch);
          console.log(`[${targetRepo}] 推送完成`);
        }
      } catch (error: any) {
        console.error(`[${targetRepo}] 合并失败:`, error.message);
        throw new Error(`分支合并失败: ${error.message}`);
      }

      // 9. 清理临时远程仓库
      console.log(`[${targetRepo}] 清理临时远程仓库...`);
      await targetGit.remote(['remove', sourceRemoteName]);

      console.log('远程分支合并完成');
    } catch (error: any) {
      // 清理临时远程仓库
      try {
        await targetGit.remote(['remove', sourceRemoteName]);
      } catch (e) {
        console.log(e);
      }

      const errorMessage = error.message || '未知错误';
      const gitError = error.git ? `\nGit错误: ${JSON.stringify(error.git, null, 2)}` : '';
      throw new Error(`远程分支合并失败: ${errorMessage}${gitError}`);
    }
  }

  /**
   * 检查远程仓库中是否存在指定分支
   * @param remoteName 远程仓库名称
   * @param branchName 分支名称
   * @param git Git实例
   */
  private async checkRemoteBranchExists(remoteName: string, branchName: string, git: SimpleGit): Promise<boolean> {
    const remoteBranches = await git.listRemote(['--heads']);

    // 规范化分支名称
    const normalizedBranchName = branchName.replace(
      /^(origin\/|remotes\/origin\/|source_[^/]+\/|refs\/heads\/|refs\/remotes\/[^/]+\/)/,
      ''
    );

    // 检查可能的分支引用格式
    const possibleRefs = [
      `refs/heads/${normalizedBranchName}`,
      `refs/remotes/source_${remoteName}/${normalizedBranchName}`,
      `refs/remotes/origin/${normalizedBranchName}`,
      `HEAD:refs/heads/${normalizedBranchName}`,
      normalizedBranchName,
      `source_${remoteName}/${normalizedBranchName}`,
      `origin/${normalizedBranchName}`,
      `refs/remotes/${remoteName}/${normalizedBranchName}`,
    ];

    // 将远程分支列表拆分为数组并规范化
    const branchList = remoteBranches
      .split('\n')
      .map((branch) => {
        const parts = branch.trim().split(/\s+/);
        return parts.length > 1 ? parts[1] : '';
      })
      .filter(Boolean);

    // 检查所有可能的引用格式
    for (const ref of possibleRefs) {
      if (branchList.includes(ref)) {
        return true;
      }
    }

    // 如果精确匹配失败，尝试模糊匹配
    const fuzzyMatch = branchList.some((branch) => {
      const normalizedRemoteBranch = branch
        .replace(/^refs\/heads\//, '')
        .replace(/^refs\/remotes\/[^/]+\//, '')
        .replace(/^(origin\/|source_[^/]+\/)/, '');
      const result = normalizedRemoteBranch === normalizedBranchName;
      return result;
    });

    // 如果模糊匹配也失败，尝试直接检查远程仓库中的分支
    if (!fuzzyMatch) {
      try {
        // 尝试直接从指定的远程仓库获取分支信息
        const specificRemoteBranches = await git.listRemote([remoteName, '--heads']);

        // 解析分支列表
        const specificBranchList = specificRemoteBranches
          .split('\n')
          .map((branch) => {
            const parts = branch.trim().split(/\s+/);
            return parts.length > 1 ? parts[1] : '';
          })
          .filter(Boolean);

        // 检查分支是否存在
        const directMatch = specificBranchList.some((branch) => {
          const remoteBranchName = branch.replace(/^refs\/heads\//, '');
          const result = remoteBranchName === normalizedBranchName;
          return result;
        });

        if (directMatch) {
          return true;
        }
      } catch (error) {
        console.warn(`从远程仓库 ${remoteName} 获取分支信息失败:`, error);
        // 错误不影响整体流程，继续后续检查
      }
    }
    return fuzzyMatch;
  }
}
